/**
 * StudyMetricsSummary - Tier-1 Glanceable Metrics Bar
 * Shows key study metrics at a glance with click-to-drill-down.
 */

import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  People,
  CheckCircle,
  Warning,
  HourglassEmpty,
  CloudDownload,
} from "@mui/icons-material";
import type { Participant, PendingParticipant } from "../../types";

interface MetricCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  color,
  onClick,
}) => (
  <Card
    sx={{
      height: "100%",
      borderTop: `3px solid ${color}`,
    }}
  >
    <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
      <CardContent sx={{ textAlign: "center", py: 2 }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {label}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

/** Determine if a participant "needs attention" based on available fields. */
export function needsAttention(participant: Participant): boolean {
  // Inactive: no authorization or expired tokens
  if (!participant.authorizationStatus) return true;
  if (participant.fitbitTokens?.accessToken && participant.fitbitTokens.expiresAt) {
    const expiry = new Date(participant.fitbitTokens.expiresAt);
    if (expiry <= new Date()) return true;
  }

  // No data source connected
  const hasDataSource =
    !!participant.fitbitTokens?.accessToken || !!participant.healthkit?.authorized;
  if (!hasDataSource) return true;

  // Not verified
  if (!participant.verified) return true;

  // Historical extraction stalled
  if (
    participant.historicalExtraction?.status === "in_progress" &&
    participant.historicalExtraction?.consecutiveNoDataDays &&
    participant.historicalExtraction.consecutiveNoDataDays > 7
  )
    return true;

  return false;
}

/** Replicate getParticipantStatus logic from StudyDashboard for active count. */
function isActiveParticipant(participant: Participant): boolean {
  if (!participant.authorizationStatus) return false;
  if (participant.fitbitTokens?.accessToken) {
    if (participant.fitbitTokens.expiresAt) {
      return new Date(participant.fitbitTokens.expiresAt) > new Date();
    }
    return true;
  }
  if (participant.healthkit?.authorized) return true;
  return !!participant.authorizationStatus;
}

interface DataExtractionJob {
  status: "pending" | "running" | "completed" | "failed";
}

interface StudyMetricsSummaryProps {
  participants: Participant[];
  pendingParticipants: PendingParticipant[];
  extractionJobs?: DataExtractionJob[];
  loading: boolean;
  onMetricClick?: (tabIndex: number) => void;
}

const StudyMetricsSummary: React.FC<StudyMetricsSummaryProps> = ({
  participants,
  pendingParticipants,
  extractionJobs = [],
  loading,
  onMetricClick,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[...Array(5)].map((_, i) => (
          <Grid item xs={6} sm={4} md key={i}>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const activeCount = participants.filter(isActiveParticipant).length;
  const attentionCount = participants.filter(needsAttention).length;
  const runningJobs = extractionJobs.filter((j) => j.status === "running").length;

  const metrics: MetricCardProps[] = [
    {
      label: "Total Enrolled",
      value: participants.length,
      icon: <People />,
      color: theme.palette.primary.main,
      onClick: () => onMetricClick?.(0),
    },
    {
      label: "Active",
      value: activeCount,
      icon: <CheckCircle />,
      color: theme.palette.success.main,
      onClick: () => onMetricClick?.(0),
    },
    {
      label: "Needs Attention",
      value: attentionCount,
      icon: <Warning />,
      color: attentionCount > 0 ? theme.palette.warning.main : theme.palette.success.main,
      onClick: () => onMetricClick?.(0),
    },
    {
      label: "Pending",
      value: pendingParticipants.length,
      icon: <HourglassEmpty />,
      color: theme.palette.info.main,
      onClick: () => onMetricClick?.(2),
    },
    {
      label: "Extractions Running",
      value: runningJobs,
      icon: <CloudDownload />,
      color: runningJobs > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
      onClick: () => onMetricClick?.(1),
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {metrics.map((m) => (
        <Grid item xs={6} sm={4} md key={m.label}>
          <MetricCard {...m} />
        </Grid>
      ))}
    </Grid>
  );
};

export default StudyMetricsSummary;
